import { PageController } from '@client/components/_core/layout/PageController';
import { Button as ModernButton } from '@client/components/_ui/data-entry/ButtonModern';
import { BetaTag } from '@client/components/_ui/display/BetaTag';
import { Space } from '@client/components/_ui/layout/Space';
import { useThemeStore } from '@client/stores/themeStore';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Divider, Switch, Tag, Typography } from 'antd';
import {
    AlertTriangle,
    Bell,
    Bookmark,
    Calendar,
    Check,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Edit,
    ExternalLink,
    Eye,
    MessageSquare,
    Moon,
    Pencil,
    Plus,
    RefreshCw,
    Send,
    Settings,
    Sparkles,
    Star,
    Sun,
    Trash2,
    Wand2,
} from 'lucide-react';

const { Title, Text, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/component-showcase/button-variants')({
    staticData: {
        breadcrumb: { title: 'Button variants' },
        featureFlag: 'enable-debug-info',
    },
    component: ButtonVariantsShowcase,
});

function ContrastTestSection() {
    const backgrounds = [
        {
            name: 'Card Header',
            description: 'Default card header background',
            className: 'bg-card border-b border-separator',
            textClass: 'text-foreground',
        },
        {
            name: 'Muted Background',
            description: 'Gray/muted areas like table headers',
            className: 'bg-muted',
            textClass: 'text-foreground',
        },
        {
            name: 'Dark Surface',
            description: 'Darker areas like sidebars or overlays',
            className: 'bg-gray-800 dark:bg-gray-900',
            textClass: 'text-white',
        },
        {
            name: 'Accent Tinted',
            description: 'Lightly tinted accent background',
            className: 'bg-accent-50',
            textClass: 'text-foreground',
        },
    ];

    return (
        <Card
            title={
                <div>
                    <Title level={4} className="!mb-0">
                        Contrast Testing
                    </Title>
                    <Text type="secondary" className="text-sm">
                        Modern Button Component on different background colours to verify contrast
                    </Text>
                </div>
            }
            className="mb-6"
        >
            <div className="space-y-6">
                {backgrounds.map((bg) => (
                    <div key={bg.name}>
                        <Text strong className="mb-2 block">
                            {bg.name}
                        </Text>
                        <Text type="secondary" className="mb-3 block text-sm">
                            {bg.description}
                        </Text>

                        {/* Background test area */}
                        <div className={`${bg.className} rounded-lg p-4`}>
                            <div className="mb-4 flex items-center justify-between">
                                <Text className={bg.textClass}>Section Title</Text>
                                <Space size="small">
                                    <ModernButton type="tertiary" size="small" icon={<Plus />}>
                                        Add item
                                    </ModernButton>
                                    <ModernButton type="secondary" size="small" icon={<Edit />} />
                                </Space>
                            </div>

                            {/* Button variants on this background */}
                            <div className="grid grid-cols-4 gap-4 border-separator/50 border-t pt-3">
                                <div>
                                    <Text type="secondary" className="mb-2 block text-xs">
                                        Primary
                                    </Text>
                                    <ModernButton type="primary" size="small">
                                        Primary
                                    </ModernButton>
                                </div>
                                <div>
                                    <Text type="secondary" className="mb-2 block text-xs">
                                        Secondary
                                    </Text>
                                    <ModernButton type="secondary" size="small">
                                        Secondary
                                    </ModernButton>
                                </div>
                                <div>
                                    <Text type="secondary" className="mb-2 block text-xs">
                                        Tertiary
                                    </Text>
                                    <ModernButton type="tertiary" size="small" icon={<Plus />}>
                                        Add
                                    </ModernButton>
                                </div>
                                <div>
                                    <Text type="secondary" className="mb-2 block text-xs">
                                        Danger
                                    </Text>
                                    <ModernButton type="danger" size="small">
                                        Delete
                                    </ModernButton>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Divider />

            {/* WCAG Guidelines */}
            <div>
                <Text strong className="mb-2 block">
                    WCAG Contrast Guidelines
                </Text>
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
                    <li>
                        <strong>AA (minimum):</strong> 4.5:1 for normal text, 3:1 for large text and UI components
                    </li>
                    <li>
                        <strong>AAA (enhanced):</strong> 7:1 for normal text, 4.5:1 for large text
                    </li>
                    <li>Button borders and text should meet at least 3:1 contrast with their background</li>
                    <li>Focus states should be clearly visible with sufficient contrast</li>
                </ul>
            </div>
        </Card>
    );
}

function ModernButtonComponent() {
    return (
        <Card
            title={
                <div>
                    <Title level={4} className="!mb-0">
                        Modern Button Component
                    </Title>
                    <Text type="secondary" className="text-sm">
                        HTML-based Button with Modern Minimal styling
                    </Text>
                </div>
            }
            className="mb-6"
        >
            <Paragraph className="mb-4 text-muted-foreground italic">
                Import from{' '}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                    @client/components/_ui/data-entry/ButtonModern
                </code>
            </Paragraph>

            {/* Primary Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Primary Buttons
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="primary" icon={<Plus />}>
                        Create New
                    </ModernButton>
                    <ModernButton type="primary">Save changes</ModernButton>
                    <ModernButton type="primary" size="micro">
                        Micro
                    </ModernButton>
                    <ModernButton type="primary" size="small">
                        Small
                    </ModernButton>
                    <ModernButton type="primary" size="large">
                        Large
                    </ModernButton>
                    <ModernButton type="primary" loading>
                        Loading
                    </ModernButton>
                    <ModernButton type="primary" disabled>
                        Disabled
                    </ModernButton>
                </Space>
            </div>

            {/* Coloured Primary Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Coloured Primary Buttons
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Use the <code className="rounded bg-muted px-1 py-0.5 text-xs">color</code> prop on primary buttons
                    for solid coloured backgrounds. Ideal for semantic actions like approve, submit, or delete.
                </Text>

                {/* All colours */}
                <div className="mb-4">
                    <Text type="secondary" className="mb-2 block text-xs">
                        All Colours
                    </Text>
                    <Space wrap size="middle">
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="primary" color="accent" icon={<Star />}>
                                Accent
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="primary" color="red" icon={<Trash2 />}>
                                Red
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="primary" color="orange" icon={<Bell />}>
                                Orange
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="primary" color="gold" icon={<Star />}>
                                Gold
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="primary" color="green" icon={<Check />}>
                                Green
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="primary" color="cyan" icon={<RefreshCw />}>
                                Cyan
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="primary" color="blue" icon={<Send />}>
                                Blue
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="primary" color="purple" icon={<Bookmark />}>
                                Purple
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="primary" color="magenta" icon={<AlertTriangle />}>
                                Magenta
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="primary" color="gray" icon={<Settings />}>
                                Gray
                            </ModernButton>
                        </div>
                    </Space>
                </div>

                {/* Use case: Semantic Actions */}
                <div className="mb-4">
                    <Text type="secondary" className="mb-2 block text-xs">
                        Use Case: Semantic Actions
                    </Text>
                    <Space wrap size="small">
                        <ModernButton type="primary" color="green" icon={<Check />}>
                            Approve
                        </ModernButton>
                        <ModernButton type="primary" color="blue" icon={<Send />}>
                            Submit
                        </ModernButton>
                        <ModernButton type="primary" color="red" icon={<Trash2 />}>
                            Delete
                        </ModernButton>
                        <ModernButton type="primary" color="orange" icon={<AlertTriangle />}>
                            Escalate
                        </ModernButton>
                    </Space>
                </div>

                {/* Sizes */}
                <div>
                    <Text type="secondary" className="mb-2 block text-xs">
                        Sizes
                    </Text>
                    <Space wrap size="middle">
                        <ModernButton type="primary" color="blue" size="micro" icon={<Send />}>
                            Micro
                        </ModernButton>
                        <ModernButton type="primary" color="blue" size="small" icon={<Send />}>
                            Small
                        </ModernButton>
                        <ModernButton type="primary" color="blue" size="middle" icon={<Send />}>
                            Middle
                        </ModernButton>
                        <ModernButton type="primary" color="blue" size="large" icon={<Send />}>
                            Large
                        </ModernButton>
                    </Space>
                </div>
            </div>

            {/* Secondary Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Secondary Buttons
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="secondary" icon={<Settings />}>
                        Settings
                    </ModernButton>
                    <ModernButton type="secondary">Cancel</ModernButton>
                    <ModernButton type="secondary" size="micro">
                        Micro
                    </ModernButton>
                    <ModernButton type="secondary" size="small">
                        Small
                    </ModernButton>
                    <ModernButton type="secondary" size="large">
                        Large
                    </ModernButton>
                    <ModernButton type="secondary" disabled>
                        Disabled
                    </ModernButton>
                    <ModernButton type="secondary" icon={<Edit />} />
                </Space>
            </div>

            {/* Coloured Secondary Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Coloured Secondary Buttons
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Use the <code className="rounded bg-muted px-1 py-0.5 text-xs">color</code> prop on secondary
                    buttons for coloured outline styles. Ideal for filter chips, status indicators, and categorised
                    actions.
                </Text>

                {/* All colours */}
                <div className="mb-4">
                    <Text type="secondary" className="mb-2 block text-xs">
                        All Colours
                    </Text>
                    <Space wrap size="middle">
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="secondary" color="accent" icon={<Star />}>
                                Accent
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="secondary" color="red" icon={<Trash2 />}>
                                Red
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="secondary" color="orange" icon={<Bell />}>
                                Orange
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="secondary" color="gold" icon={<Star />}>
                                Gold
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="secondary" color="green" icon={<Check />}>
                                Green
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="secondary" color="cyan" icon={<RefreshCw />}>
                                Cyan
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="secondary" color="blue" icon={<Settings />}>
                                Blue
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="secondary" color="purple" icon={<Bookmark />}>
                                Purple
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="secondary" color="magenta" icon={<AlertTriangle />}>
                                Magenta
                            </ModernButton>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton type="secondary" color="gray" icon={<Settings />}>
                                Gray
                            </ModernButton>
                        </div>
                    </Space>
                </div>

                {/* Use case: Quick Filters */}
                <div className="mb-4">
                    <Text type="secondary" className="mb-2 block text-xs">
                        Use Case: Quick Filter Chips
                    </Text>
                    <Space wrap size="small">
                        <ModernButton type="secondary" size="small">
                            All Users (67)
                        </ModernButton>
                        <ModernButton type="secondary" size="small" color="green" icon={<Check size={14} />}>
                            Active
                        </ModernButton>
                        <ModernButton type="secondary" size="small" color="red" icon={<Eye size={14} />}>
                            Disabled
                        </ModernButton>
                        <ModernButton type="secondary" size="small" color="purple" icon={<Pencil size={14} />}>
                            Admins
                        </ModernButton>
                    </Space>
                </div>

                {/* Sizes */}
                <div>
                    <Text type="secondary" className="mb-2 block text-xs">
                        Sizes
                    </Text>
                    <Space wrap size="middle">
                        <ModernButton type="secondary" color="green" size="micro" icon={<Check />}>
                            Micro
                        </ModernButton>
                        <ModernButton type="secondary" color="green" size="small" icon={<Check />}>
                            Small
                        </ModernButton>
                        <ModernButton type="secondary" color="green" size="middle" icon={<Check />}>
                            Middle
                        </ModernButton>
                        <ModernButton type="secondary" color="green" size="large" icon={<Check />}>
                            Large
                        </ModernButton>
                    </Space>
                </div>
            </div>

            {/* Tertiary Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Tertiary Buttons
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="tertiary" icon={<Plus />}>
                        Add Filter
                    </ModernButton>
                    <ModernButton type="tertiary">Clear All</ModernButton>
                    <ModernButton type="tertiary" size="micro">
                        Micro
                    </ModernButton>
                    <ModernButton type="tertiary" size="small">
                        Small
                    </ModernButton>
                    <ModernButton type="tertiary" disabled>
                        Disabled
                    </ModernButton>
                </Space>
            </div>

            {/* Dashed Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Dashed Buttons (Add / create into an empty slot)
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Use via <code>{'Button.Add variant="dashed"'}</code> for add actions in an empty slot or directly
                    above a droppable region. Not a general-purpose button style.
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="dashed">New item</ModernButton>
                    <ModernButton type="dashed" size="small">
                        Small
                    </ModernButton>
                    <ModernButton type="dashed" disabled>
                        Disabled
                    </ModernButton>
                </Space>
            </div>

            {/* Danger Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Danger Buttons (Outlined)
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="danger" icon={<Trash2 />}>
                        Delete
                    </ModernButton>
                    <ModernButton type="danger">Remove</ModernButton>
                    <ModernButton type="danger" size="micro">
                        Micro
                    </ModernButton>
                    <ModernButton type="danger" size="small">
                        Small
                    </ModernButton>
                    <ModernButton type="danger" disabled>
                        Disabled
                    </ModernButton>
                </Space>
            </div>

            {/* Danger Filled Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Danger Filled Buttons (Solid)
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Use for urgent attention-needed actions, not destructive actions.
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="danger-filled" icon={<Wand2 />}>
                        Review Breaches
                    </ModernButton>
                    <ModernButton type="danger-filled" icon={<AlertTriangle />}>
                        Urgent Action
                    </ModernButton>
                    <ModernButton type="danger-filled" size="micro">
                        Micro
                    </ModernButton>
                    <ModernButton type="danger-filled" size="small">
                        Small
                    </ModernButton>
                    <ModernButton type="danger-filled" size="large">
                        Large
                    </ModernButton>
                    <ModernButton type="danger-filled" loading>
                        Loading
                    </ModernButton>
                    <ModernButton type="danger-filled" disabled>
                        Disabled
                    </ModernButton>
                </Space>
            </div>

            {/* Danger Outline Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Danger outline buttons
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Use for a terminal, non-destructive action that ends a record's workflow, such as "Close complaint"
                    on an entity detail toolbar.
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="danger-outline" icon={<CheckCircle />}>
                        Close complaint
                    </ModernButton>
                    <ModernButton type="danger-outline" size="small">
                        Small
                    </ModernButton>
                    <ModernButton type="danger-outline" loading>
                        Loading
                    </ModernButton>
                    <ModernButton type="danger-outline" disabled>
                        Disabled
                    </ModernButton>
                </Space>
            </div>

            {/* Link Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Link Buttons
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="link">View Details</ModernButton>
                    <ModernButton type="link" icon={<ChevronRight />} iconPosition="end">
                        Learn More
                    </ModernButton>
                    <ModernButton type="link" size="micro">
                        Micro Link
                    </ModernButton>
                    <ModernButton type="link" size="small">
                        Small Link
                    </ModernButton>
                    <ModernButton type="link" disabled>
                        Disabled
                    </ModernButton>
                </Space>
            </div>

            {/* Icon-only Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Icon Buttons (Bordered)
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="secondary" icon={<Plus />} />
                    <ModernButton type="secondary" icon={<Edit />} />
                    <ModernButton type="secondary" icon={<Trash2 />} />
                    <ModernButton type="secondary" icon={<Settings />} />
                    <ModernButton type="secondary" icon={<Plus />} size="micro" />
                    <ModernButton type="secondary" icon={<Plus />} size="small" />
                    <ModernButton type="secondary" icon={<Plus />} size="large" />
                </Space>
            </div>

            {/* Tertiary Icon Buttons */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Tertiary Icon Buttons (Borderless)
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Minimal, borderless icon buttons. Use{' '}
                    <code className="rounded bg-muted px-1 py-0.5 text-xs">type=&quot;tertiary&quot;</code> with
                    icon-only buttons.
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="tertiary" icon={<Plus />} />
                    <ModernButton type="tertiary" icon={<Edit />} />
                    <ModernButton type="tertiary" icon={<Trash2 />} />
                    <ModernButton type="tertiary" icon={<Settings />} />
                    <ModernButton type="tertiary" icon={<Plus />} size="micro" />
                    <ModernButton type="tertiary" icon={<Plus />} size="small" />
                    <ModernButton type="tertiary" icon={<Plus />} size="large" />
                </Space>
            </div>

            {/* Danger Modifier */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Danger Modifier
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Use the <code className="rounded bg-muted px-1 py-0.5 text-xs">danger</code> prop to apply danger
                    (red) styling to any button type. Ideal for delete actions in tables.
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="tertiary" icon={<Trash2 />} danger />
                    <ModernButton type="secondary" icon={<Trash2 />} danger />
                    <ModernButton type="tertiary" danger>
                        Delete
                    </ModernButton>
                    <ModernButton type="secondary" danger>
                        Remove Item
                    </ModernButton>
                    <ModernButton type="tertiary" icon={<Trash2 />} size="small" danger />
                </Space>
            </div>

            {/* Icon Position */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Icon Position
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="primary" icon={<Plus />} iconPosition="start">
                        Icon Start
                    </ModernButton>
                    <ModernButton type="primary" icon={<ChevronRight />} iconPosition="end">
                        Icon End
                    </ModernButton>
                </Space>
            </div>

            {/* Block Button */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Block Button
                </Text>
                <div className="max-w-md">
                    <ModernButton type="primary" block>
                        Full Width Button
                    </ModernButton>
                </div>
            </div>

            {/* Beta Prop */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Beta Prop
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Use the <code className="rounded bg-muted px-1 py-0.5 text-xs">beta</code> boolean prop for a
                    convenient BETA badge.
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="tertiary" icon={<Wand2 />} beta>
                        Analyse complaint
                    </ModernButton>
                    <ModernButton type="secondary" icon={<Sparkles />} beta>
                        AI Suggestions
                    </ModernButton>
                    <ModernButton type="primary" beta>
                        New Feature
                    </ModernButton>
                </Space>
            </div>

            {/* Buttons with Custom Badges */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Custom Badges
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Use the <code className="rounded bg-muted px-1 py-0.5 text-xs">badge</code> prop for custom badges
                    (non-beta).
                </Text>
                <Space wrap size="middle">
                    <ModernButton type="tertiary" icon={<Calendar />} badge={<BetaTag />}>
                        Assistant
                    </ModernButton>
                    <ModernButton type="tertiary" icon={<MessageSquare />} badge={<BetaTag />}>
                        AI Chat
                    </ModernButton>
                    <ModernButton type="tertiary" icon={<Sparkles />} badge={<BetaTag />}>
                        Analyse incident
                    </ModernButton>
                    <ModernButton
                        type="secondary"
                        icon={<Plus />}
                        badge={
                            <Tag color="green" className="px-1 py-0 text-xs">
                                NEW
                            </Tag>
                        }
                    >
                        New Feature
                    </ModernButton>
                </Space>
            </div>

            {/* Subcomponents - Table Row Actions */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Table Row Action Subcomponents
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Pre-configured subcomponents for common table row actions. Each has a button mode (onClick) and a
                    .Link mode for type-safe TanStack Router navigation.
                </Text>

                {/* Button mode (onClick) */}
                <div className="mb-4">
                    <Text type="secondary" className="mb-2 block text-xs">
                        Button Mode (onClick)
                    </Text>
                    <Space wrap size="middle">
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.View onClick={() => {}} />
                            <Text type="secondary" className="text-xs">
                                Button.View
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Edit onClick={() => {}} />
                            <Text type="secondary" className="text-xs">
                                Button.Edit
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Delete onConfirm={(onSuccess) => onSuccess()} />
                            <Text type="secondary" className="text-xs">
                                Button.Delete
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Refresh onClick={() => {}} />
                            <Text type="secondary" className="text-xs">
                                Button.Refresh
                            </Text>
                        </div>
                    </Space>
                </div>

                {/* Router Link mode (.Link) */}
                <div className="mb-4">
                    <Text type="secondary" className="mb-2 block text-xs">
                        Router Mode (.Link) - Type-safe TanStack Router navigation
                    </Text>
                    <Space wrap size="middle">
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.View.Link
                                to="/console/$orgId/component-showcase"
                                params={{ orgId: 1n }}
                                tooltip="View details"
                            />
                            <Text type="secondary" className="text-xs">
                                Button.View.Link
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Edit.Link
                                to="/console/$orgId/component-showcase"
                                params={{ orgId: 1n }}
                                tooltip="Edit"
                            />
                            <Text type="secondary" className="text-xs">
                                Button.Edit.Link
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Refresh.Link
                                to="/console/$orgId/component-showcase"
                                params={{ orgId: 1n }}
                                tooltip="Refresh page"
                            />
                            <Text type="secondary" className="text-xs">
                                Button.Refresh.Link
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.OpenInNewTab
                                to="/console/$orgId/component-showcase"
                                params={{ orgId: 1n }}
                                tooltip="Open in new tab"
                            />
                            <Text type="secondary" className="text-xs">
                                Button.OpenInNewTab
                            </Text>
                        </div>
                    </Space>
                </div>
            </div>

            {/* Coloured Icon Button */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Coloured Icon Button (Button.Icon)
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Icon buttons with customisable colours. Available in bordered and borderless (default) variants.
                </Text>

                {/* Borderless variants */}
                <div className="mb-4">
                    <Text type="secondary" className="mb-2 block text-xs">
                        Borderless (default)
                    </Text>
                    <Space wrap size="middle">
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Star />} color="accent" />
                            <Text type="secondary" className="text-xs">
                                accent
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Trash2 />} color="red" />
                            <Text type="secondary" className="text-xs">
                                red
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Bell />} color="orange" />
                            <Text type="secondary" className="text-xs">
                                orange
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Star />} color="gold" />
                            <Text type="secondary" className="text-xs">
                                gold
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Check />} color="green" />
                            <Text type="secondary" className="text-xs">
                                green
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<ExternalLink />} color="cyan" />
                            <Text type="secondary" className="text-xs">
                                cyan
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Settings />} color="blue" />
                            <Text type="secondary" className="text-xs">
                                blue
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Bookmark />} color="purple" />
                            <Text type="secondary" className="text-xs">
                                purple
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<AlertTriangle />} color="magenta" />
                            <Text type="secondary" className="text-xs">
                                magenta
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Settings />} color="gray" />
                            <Text type="secondary" className="text-xs">
                                gray
                            </Text>
                        </div>
                    </Space>
                </div>

                {/* Bordered variants */}
                <div className="mb-4">
                    <Text type="secondary" className="mb-2 block text-xs">
                        Bordered
                    </Text>
                    <Space wrap size="middle">
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Star />} color="accent" bordered />
                            <Text type="secondary" className="text-xs">
                                accent
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Trash2 />} color="red" bordered />
                            <Text type="secondary" className="text-xs">
                                red
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Bell />} color="orange" bordered />
                            <Text type="secondary" className="text-xs">
                                orange
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Star />} color="gold" bordered />
                            <Text type="secondary" className="text-xs">
                                gold
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Check />} color="green" bordered />
                            <Text type="secondary" className="text-xs">
                                green
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<ExternalLink />} color="cyan" bordered />
                            <Text type="secondary" className="text-xs">
                                cyan
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Settings />} color="blue" bordered />
                            <Text type="secondary" className="text-xs">
                                blue
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Bookmark />} color="purple" bordered />
                            <Text type="secondary" className="text-xs">
                                purple
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon
                                aria-label="Icon button"
                                icon={<AlertTriangle />}
                                color="magenta"
                                bordered
                            />
                            <Text type="secondary" className="text-xs">
                                magenta
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Settings />} color="gray" bordered />
                            <Text type="secondary" className="text-xs">
                                gray
                            </Text>
                        </div>
                    </Space>
                </div>

                {/* Sizes */}
                <div className="mb-4">
                    <Text type="secondary" className="mb-2 block text-xs">
                        Sizes
                    </Text>
                    <Space wrap size="middle">
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Star />} color="gold" size="micro" />
                            <Text type="secondary" className="text-xs">
                                micro
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Star />} color="gold" size="small" />
                            <Text type="secondary" className="text-xs">
                                small
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Star />} color="gold" size="middle" />
                            <Text type="secondary" className="text-xs">
                                middle
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon aria-label="Icon button" icon={<Star />} color="gold" size="large" />
                            <Text type="secondary" className="text-xs">
                                large
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon
                                aria-label="Icon button"
                                icon={<Star />}
                                color="blue"
                                size="micro"
                                bordered
                            />
                            <Text type="secondary" className="text-xs">
                                micro bordered
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon
                                aria-label="Icon button"
                                icon={<Star />}
                                color="blue"
                                size="small"
                                bordered
                            />
                            <Text type="secondary" className="text-xs">
                                small bordered
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon
                                aria-label="Icon button"
                                icon={<Star />}
                                color="blue"
                                size="middle"
                                bordered
                            />
                            <Text type="secondary" className="text-xs">
                                middle bordered
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon
                                aria-label="Icon button"
                                icon={<Star />}
                                color="blue"
                                size="large"
                                bordered
                            />
                            <Text type="secondary" className="text-xs">
                                large bordered
                            </Text>
                        </div>
                    </Space>
                </div>

                {/* Router Link mode */}
                <div>
                    <Text type="secondary" className="mb-2 block text-xs">
                        Router Mode (.Link) - Type-safe navigation
                    </Text>
                    <Space wrap size="middle">
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon.Link
                                icon={<Star />}
                                color="gold"
                                to="/console/$orgId/component-showcase"
                                params={{ orgId: 1n }}
                                tooltip="Favourites"
                            />
                            <Text type="secondary" className="text-xs">
                                Button.Icon.Link
                            </Text>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <ModernButton.Icon.Link
                                icon={<Settings />}
                                color="blue"
                                bordered
                                to="/console/$orgId/component-showcase"
                                params={{ orgId: 1n }}
                                tooltip="Settings"
                            />
                            <Text type="secondary" className="text-xs">
                                bordered
                            </Text>
                        </div>
                    </Space>
                </div>
            </div>

            {/* Button Grouping with Space */}
            <div className="mb-6">
                <Text strong className="mb-3 block">
                    Button Grouping
                </Text>
                <Text type="secondary" className="mb-3 block text-sm">
                    Use the <code className="rounded bg-muted px-1 py-0.5 text-xs">Space</code> component from{' '}
                    <code className="rounded bg-muted px-1 py-0.5 text-xs">@client/components/_ui/layout/Space</code> to
                    group buttons with consistent spacing.
                </Text>
                <Space wrap size="large">
                    <div>
                        <Text type="secondary" className="mb-2 block text-xs">
                            Form Footer Pattern
                        </Text>
                        <Space size="small">
                            <ModernButton type="secondary">Cancel</ModernButton>
                            <ModernButton type="primary">Save</ModernButton>
                        </Space>
                    </div>
                    <div>
                        <Text type="secondary" className="mb-2 block text-xs">
                            Navigation Buttons
                        </Text>
                        <Space size="small">
                            <ModernButton type="secondary" icon={<ChevronLeft />} />
                            <ModernButton type="secondary" icon={<ChevronRight />} />
                        </Space>
                    </div>
                    <div>
                        <Text type="secondary" className="mb-2 block text-xs">
                            Tertiary Small Buttons
                        </Text>
                        <Space size="none">
                            <ModernButton size="small" icon={<Plus />}>
                                Clear
                            </ModernButton>
                            <ModernButton size="small">Clear</ModernButton>
                        </Space>
                    </div>
                </Space>
            </div>

            <Divider />

            {/* API Reference */}
            <div>
                <Text strong className="mb-2 block">
                    API Reference
                </Text>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-separator border-b">
                                <th className="py-2 pr-4 text-left font-medium">Prop</th>
                                <th className="px-4 py-2 text-left font-medium">Type</th>
                                <th className="px-4 py-2 text-left font-medium">Default</th>
                                <th className="px-4 py-2 text-left font-medium">Description</th>
                            </tr>
                        </thead>
                        <tbody className="text-muted-foreground">
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">type</td>
                                <td className="px-4 py-2 font-mono text-xs">
                                    &apos;primary&apos; | &apos;secondary&apos; | &apos;tertiary&apos; |
                                    &apos;dashed&apos; | &apos;danger&apos; | &apos;danger-filled&apos; |
                                    &apos;link&apos;
                                </td>
                                <td className="px-4 py-2 font-mono text-xs">&apos;secondary&apos;</td>
                                <td className="px-4 py-2">Visual style variant</td>
                            </tr>
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">size</td>
                                <td className="px-4 py-2 font-mono text-xs">
                                    &apos;micro&apos; | &apos;small&apos; | &apos;middle&apos; | &apos;large&apos;
                                </td>
                                <td className="px-4 py-2 font-mono text-xs">&apos;middle&apos;</td>
                                <td className="px-4 py-2">Button size</td>
                            </tr>
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">icon</td>
                                <td className="px-4 py-2 font-mono text-xs">ReactNode</td>
                                <td className="px-4 py-2">-</td>
                                <td className="px-4 py-2">Icon element</td>
                            </tr>
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">iconPosition</td>
                                <td className="px-4 py-2 font-mono text-xs">&apos;start&apos; | &apos;end&apos;</td>
                                <td className="px-4 py-2 font-mono text-xs">&apos;start&apos;</td>
                                <td className="px-4 py-2">Icon position</td>
                            </tr>
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">loading</td>
                                <td className="px-4 py-2 font-mono text-xs">boolean</td>
                                <td className="px-4 py-2 font-mono text-xs">false</td>
                                <td className="px-4 py-2">Show loading spinner</td>
                            </tr>
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">htmlType</td>
                                <td className="px-4 py-2 font-mono text-xs">
                                    &apos;button&apos; | &apos;submit&apos; | &apos;reset&apos;
                                </td>
                                <td className="px-4 py-2 font-mono text-xs">&apos;button&apos;</td>
                                <td className="px-4 py-2">HTML button type</td>
                            </tr>
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">form</td>
                                <td className="px-4 py-2 font-mono text-xs">string</td>
                                <td className="px-4 py-2">-</td>
                                <td className="px-4 py-2">Form ID for external submit</td>
                            </tr>
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">block</td>
                                <td className="px-4 py-2 font-mono text-xs">boolean</td>
                                <td className="px-4 py-2 font-mono text-xs">false</td>
                                <td className="px-4 py-2">Full-width button</td>
                            </tr>
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">beta</td>
                                <td className="px-4 py-2 font-mono text-xs">boolean</td>
                                <td className="px-4 py-2 font-mono text-xs">false</td>
                                <td className="px-4 py-2">Show BETA badge after label</td>
                            </tr>
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">badge</td>
                                <td className="px-4 py-2 font-mono text-xs">ReactNode</td>
                                <td className="px-4 py-2">-</td>
                                <td className="px-4 py-2">Custom badge element after label</td>
                            </tr>
                            <tr className="border-separator border-b">
                                <td className="py-2 pr-4 font-mono text-xs">danger</td>
                                <td className="px-4 py-2 font-mono text-xs">boolean</td>
                                <td className="px-4 py-2 font-mono text-xs">false</td>
                                <td className="px-4 py-2">Apply danger (red) colour styling</td>
                            </tr>
                            <tr>
                                <td className="py-2 pr-4 font-mono text-xs">color</td>
                                <td className="px-4 py-2 font-mono text-xs">
                                    &apos;accent&apos; | &apos;red&apos; | &apos;orange&apos; | &apos;gold&apos; |
                                    &apos;green&apos; | &apos;cyan&apos; | &apos;blue&apos; | &apos;purple&apos; |
                                    &apos;magenta&apos; | &apos;gray&apos;
                                </td>
                                <td className="px-4 py-2">-</td>
                                <td className="px-4 py-2">
                                    Colour theme for primary (solid fill) and secondary (outline) buttons
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Card>
    );
}

function ButtonVariantsShowcase() {
    const { theme, setTheme } = useThemeStore();
    const isDark =
        theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-6xl p-6">
                {/* Header */}
                <div className="mb-8 flex items-start justify-between">
                    <div>
                        <Title level={2}>Button Variants Showcase</Title>
                        <Paragraph className="max-w-2xl text-muted-foreground">
                            Modern Button Component documentation and examples. A clean, HTML-based button with modern
                            minimal styling.
                        </Paragraph>
                    </div>
                    <div className="flex items-center gap-3 rounded-lg border border-separator bg-card px-4 py-2">
                        <Sun size={16} className={!isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                        <Switch
                            checked={isDark}
                            onChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                            checkedChildren={<Moon size={12} />}
                            unCheckedChildren={<Sun size={12} />}
                        />
                        <Moon size={16} className={isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                    </div>
                </div>

                <ModernButtonComponent />

                <Divider>
                    <Text type="secondary">Contrast Testing</Text>
                </Divider>

                <ContrastTestSection />
            </div>
        </PageController>
    );
}
